import { useRouter } from 'expo-router';
import { ArrowRight, Check, X } from 'lucide-react-native';
import { Fragment } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Card } from '../../../components/primitives/Card';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies, darkPrecisionTypography } from '../../../theme/typography';

interface OnboardingChecklistProps {
  totalLeads: number;
  hasStageChange: boolean;
  teamMemberCount: number;
  onDismiss: () => void;
}

interface Milestone {
  id: string;
  label: string;
  completed: boolean;
  route: string;
}

function StepPill({
  milestone,
  index,
  isNext,
}: Readonly<{ milestone: Milestone; index: number; isNext: boolean }>) {
  const router = useRouter();
  const { completed } = milestone;

  const handlePress = () => {
    if (!completed) router.push(milestone.route as never);
  };

  const borderColor = completed
    ? colors.status.success.main
    : isNext
      ? colors.primary.borderActive
      : colors.border.muted;
  const backgroundColor = isNext ? colors.primary.muted : 'transparent';
  const labelColor = completed || isNext ? colors.text.primary : colors.text.secondary;

  return (
    <Pressable
      testID={`onboarding-step-${milestone.id}`}
      onPress={handlePress}
      disabled={completed}
      accessibilityRole="button"
      accessibilityLabel={milestone.label}
      accessibilityState={{ disabled: completed, checked: completed }}
      style={({ pressed }) => [
        styles.pillBase,
        { backgroundColor, borderColor },
        pressed && !completed && styles.pillPressed,
      ]}
    >
      <HStack spacing="sm" align="center">
        <View style={[styles.indicator, completed ? styles.indicatorDone : styles.indicatorTodo]}>
          {completed ? (
            <Check size={12} color={colors['primary-foreground']} strokeWidth={3} />
          ) : (
            <Text color={colors.text.tertiary} style={styles.stepNumber}>
              {index + 1}
            </Text>
          )}
        </View>
        <Text color={labelColor} numberOfLines={1} style={[styles.label, { color: labelColor }]}>
          {milestone.label}
        </Text>
      </HStack>
    </Pressable>
  );
}

export function OnboardingChecklist({
  totalLeads,
  hasStageChange,
  teamMemberCount,
  onDismiss,
}: Readonly<OnboardingChecklistProps>) {
  const { t } = useTranslation();

  // 'setup-agent' was folded out (org-onboarding-autoconfigure Phase 5): AI
  // agent configuration is now the mandatory first-login wizard gated in
  // app/(app)/_layout.tsx for owner/admin, so it's never an optional item
  // here for anyone who can reach this dashboard.
  const milestones: Milestone[] = [
    {
      id: 'create-lead',
      label: t('onboarding.milestones.createLead'),
      completed: totalLeads > 0,
      route: '/lead/new',
    },
    {
      id: 'move-lead',
      label: t('onboarding.milestones.moveLead'),
      completed: hasStageChange,
      route: '/funnel',
    },
    {
      id: 'invite-member',
      label: t('onboarding.milestones.inviteMember'),
      completed: teamMemberCount > 1,
      route: '/settings',
    },
  ];

  const completedCount = milestones.filter((m) => m.completed).length;
  const totalMilestones = milestones.length;
  const progressPercent = completedCount / totalMilestones;
  const firstIncompleteIndex = milestones.findIndex((m) => !m.completed);

  return (
    <FadeIn delay={100} translateY={10}>
      <Card variant="default" radius="3xl" padding="lg">
        <VStack spacing="md">
          <HStack justify="between" align="center">
            <HStack spacing="sm" align="center">
              <Text
                color={colors.text.primary}
                style={[darkPrecisionTypography.heading, { color: colors.text.primary }]}
              >
                {t('onboarding.title')}
              </Text>
              <View style={styles.badge}>
                <Text
                  color={colors.text.tertiary}
                  style={[
                    darkPrecisionTypography.label,
                    { fontSize: 10, letterSpacing: 0.8, color: colors.text.tertiary },
                  ]}
                >
                  {t('onboarding.complete', { completed: completedCount, total: totalMilestones })}
                </Text>
              </View>
            </HStack>
            <Pressable
              testID="onboarding-dismiss"
              onPress={onDismiss}
              style={styles.dismissButton}
              accessibilityRole="button"
              accessibilityLabel={t('onboarding.dismiss')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <X size={18} color={colors.text.tertiary} strokeWidth={2} />
            </Pressable>
          </HStack>

          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressFill,
                { width: `${progressPercent * 100}%`, backgroundColor: colors.primary.neon },
              ]}
            />
          </View>

          <Box style={styles.pillsRow}>
            {milestones.map((milestone, index) => (
              <Fragment key={milestone.id}>
                <StepPill
                  milestone={milestone}
                  index={index}
                  isNext={index === firstIncompleteIndex}
                />
                {index < milestones.length - 1 && (
                  <View testID="onboarding-step-separator" style={styles.separator}>
                    <ArrowRight size={15} color={colors.text.tertiary} strokeWidth={2} />
                  </View>
                )}
              </Fragment>
            ))}
          </Box>
        </VStack>
      </Card>
    </FadeIn>
  );
}

const styles = StyleSheet.create({
  badge: {
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  progressTrack: {
    height: 4,
    backgroundColor: colors.background.secondary,
    width: '100%',
    borderRadius: tokens.radius.full,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: tokens.radius.full,
  },
  pillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  separator: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillBase: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    maxWidth: 260,
    ...Platform.select({
      web: {
        transition: 'transform 0.18s ease, border-color 0.18s ease',
        cursor: 'pointer',
      } as object,
      default: {},
    }),
  },
  pillPressed: {
    transform: [{ scale: 0.97 }],
  },
  indicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  indicatorDone: {
    backgroundColor: colors.status.success.main,
  },
  indicatorTodo: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.border.strong,
  },
  stepNumber: {
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: 11,
    fontWeight: '600',
  },
  label: {
    fontSize: 13,
    lineHeight: 17,
    fontWeight: '500',
  },
  dismissButton: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },
});
